import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    ListQCChecklistTemplatesParams,
    QCChecklistTemplateCreateModel,
    QCChecklistTemplateDetail,
    QCChecklistTemplateListItem,
    QCChecklistTemplateMatchResult,
    QCChecklistTemplateUpdateModel,
} from '../model/qc-checklist-template';

const basePath = '/api/qc-checklist-templates';

/**
 * HTTP client for the QC checklist template admin endpoints and the
 * inspector-side match endpoint.
 */
@Injectable({ providedIn: 'root' })
export class QCChecklistTemplateClient {
    private readonly _httpClient = inject(HttpClient);

    /**
     * Resolve the best-matching active template for a treatment.
     * Inspector flow only.
     */
    match(treatmentId: string): Observable<QCChecklistTemplateMatchResult> {
        return this._httpClient.get<QCChecklistTemplateMatchResult>(`${basePath}/match`, {
            params: new HttpParams({ fromObject: { treatmentId } }),
        });
    }

    /**
     * List templates filtered by dimension fields and optional name search.
     * Requires the read:qc-checklist-templates or manage:qc-checklist-templates permission.
     */
    list(params: ListQCChecklistTemplatesParams): Observable<QCChecklistTemplateListItem[]> {
        let httpParams = new HttpParams();
        if (params.treatmentTypeOriginalId != null) {
            httpParams = httpParams.set(
                'treatmentTypeOriginalId',
                String(params.treatmentTypeOriginalId)
            );
        }
        if (params.treatmentSubTypeOriginalId != null) {
            httpParams = httpParams.set(
                'treatmentSubTypeOriginalId',
                String(params.treatmentSubTypeOriginalId)
            );
        }
        if (params.organizationId != null) {
            httpParams = httpParams.set('organizationId', params.organizationId);
        }
        if (params.officeId != null) {
            httpParams = httpParams.set('officeId', params.officeId);
        }
        if (params.region != null) {
            httpParams = httpParams.set('region', String(params.region));
        }
        if (params.isActive != null) {
            httpParams = httpParams.set('isActive', String(params.isActive));
        }
        if (params.search != null && params.search.length > 0) {
            httpParams = httpParams.set('search', params.search);
        }
        return this._httpClient.get<QCChecklistTemplateListItem[]>(basePath, {
            params: httpParams,
        });
    }

    /** Get a single template detail by id. */
    getById(id: number): Observable<QCChecklistTemplateDetail> {
        return this._httpClient.get<QCChecklistTemplateDetail>(`${basePath}/${id}`);
    }

    /** Create a new template. Requires the manage:qc-checklist-templates permission. */
    create(model: QCChecklistTemplateCreateModel): Observable<QCChecklistTemplateDetail> {
        return this._httpClient.post<QCChecklistTemplateDetail>(basePath, model);
    }

    /** Replace an existing template's whole body and dimensions. Requires admin permission. */
    update(
        id: number,
        model: QCChecklistTemplateUpdateModel
    ): Observable<QCChecklistTemplateDetail> {
        return this._httpClient.put<QCChecklistTemplateDetail>(`${basePath}/${id}`, model);
    }

    /** Toggle a template's active flag. Requires admin permission. */
    setActive(id: number, isActive: boolean): Observable<QCChecklistTemplateDetail> {
        return this._httpClient.patch<QCChecklistTemplateDetail>(
            `${basePath}/${id}/active`,
            isActive
        );
    }

    /** Soft-delete a template. Requires admin permission. */
    delete(id: number): Observable<void> {
        return this._httpClient.delete<void>(`${basePath}/${id}`);
    }
}
